import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Users, UsersRound, CheckCircle2, Clock, Ban, TrendingUp, Trash2, Eye, MapPin, Hammer, HardHat, Search, Pencil, X, Loader2, Download, Mail, KeyRound, AlertTriangle, AlertCircle, Save, Printer, FileSpreadsheet, FileUp, Bell, Send, BarChart3, Briefcase, Building2, Map, Calendar, Wheat, FileText, ShieldCheck, Zap, UserPlus, UserCog, Megaphone, ChevronRight, TrendingDown, Star, Wrench, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ComposedChart, Line as ReLine, LineChart, Line } from "recharts";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import FileUpload from "../components/FileUpload";
import PushToggle from "../components/PushToggle";
import WorkmenDrawer from "../components/WorkmenDrawer";
import WorkmenList from "../components/WorkmenList";

import IndiaChoropleth from "../components/dashboard/IndiaChoropleth";
import TopStateMap from "../components/dashboard/TopStateMap";
import DailyQuote from "../components/dashboard/DailyQuote";
import DeployHeartbeat from "../components/dashboard/DeployHeartbeat";
import StateWeather from "../components/dashboard/StateWeather";
import { imageForCity } from "../components/dashboard/cityImages";

// Mobile-only quick access boxes: Status / Reports / Pending Approvals
function MobileQuickNav({ user }) {
  const canP = (p) => !!user && (user.is_super || (user.permissions || []).includes(p));
  const [pending, setPending] = useState(0);
  useEffect(() => {
    if (!user) return;
    // The quick-nav bar is mobile-only (md:hidden). Skip these (heavy) count
    // queries entirely on desktop/tablet to avoid piling load onto the
    // already-heavy dashboard — this was a source of transient 500s in prod.
    const isMobile = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 767px)").matches : false;
    if (!isMobile) return;
    let cancelled = false;
    const fetchCounts = async () => {
      let total = 0;
      try {
        if (canP("approve_workmen") || canP("approve_medical")) {
          const r = await api.get("/workmen/pending", { silent: true });
          total += (r.data || []).length;
        }
        if (canP("approve_payroll_roster") || canP("approve_medical")) {
          const r = await api.get("/pay-roster/pending", { silent: true });
          total += (r.data || []).length;
        }
        if (canP("approve_contractors")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: false, include_pending: true } });
          total += (r.data || []).length;
        }
        if (canP("approve_payroll")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: true, include_pending: true } });
          total += (r.data || []).length;
        }
      } catch { /* silent */ }
      if (!cancelled) setPending(total);
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 180000);
    return () => { cancelled = true; clearInterval(t); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [user]);
  const boxCls = "flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-white/20 bg-white/10 px-2 py-3 text-center backdrop-blur-sm transition-colors active:bg-white/20";
  return (
    <div className="mb-5 grid grid-cols-3 gap-2 md:hidden" data-testid="mobile-quick-nav">
      {user?.is_super ? (
        <Link to="/status" className={boxCls} data-testid="mobile-quick-status">
          <Zap className="h-5 w-5 text-ncc-gold2" />
          <span className="industrial-label text-[10px] text-white">Status</span>
        </Link>
      ) : (
        <Link to="/reports?tab=workmen-roster" className={boxCls} data-testid="mobile-quick-roster">
          <UsersRound className="h-5 w-5 text-ncc-gold2" />
          <span className="industrial-label text-[10px] text-white">Roster</span>
        </Link>
      )}
      <Link to="/reports" className={boxCls} data-testid="mobile-quick-reports">
        <BarChart3 className="h-5 w-5 text-ncc-gold2" />
        <span className="industrial-label text-[10px] text-white">Reports</span>
      </Link>
      <Link to="/reports?tab=approvals" className={`${boxCls} relative`} data-testid="mobile-quick-approvals">
        <CheckCircle2 className="h-5 w-5 text-ncc-gold2" />
        <span className="industrial-label text-[10px] text-white">Pending Approvals</span>
        {pending > 0 && (
          <span className="absolute -top-1.5 -right-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border border-white bg-red-600 px-1 font-mono text-[10px] font-bold text-white" data-testid="mobile-quick-approvals-count">
            {pending}
          </span>
        )}
      </Link>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const canViewStats = !!user && (user.is_super || (user.permissions || []).includes("view_stats"));
  const [segment, setSegment] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!canViewStats) return;  // Dashboard endpoint requires view_stats; skip silently for limited users
    setBusy(true);
    try {
      const r = await api.get("/dashboard", { params: { segment } });
      setData(r.data);
    } catch (err) {
      console.error("dashboard load failed", err);
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Failed to load dashboard");
      }
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, segment]);

  // Apply dark body bg only on dashboard mount; revert on unmount.
  useEffect(() => {
    document.body.classList.add("admin-dashboard-bg");
    return () => document.body.classList.remove("admin-dashboard-bg");
  }, []);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  // Front-page dashboard (KPI tiles, workforce map, weather etc.) is reserved
  // for Admin / Project Co-Ordinator / Project Incharge — anyone whose role
  // does NOT include `view_stats` gets auto-routed to their first available
  // page based on the permissions they DO hold.
  if (!canViewStats) {
    const perms = new Set(user.permissions || []);
    let target = "/directory";
    if (perms.has("approve_workmen") || perms.has("workmen_roster_approval")) target = "/admin/workmen-approvals";
    else if (perms.has("approve_contractors") || perms.has("reject_contractors")) target = "/admin/contractor-approvals";
    else if (perms.has("approve_payroll") || perms.has("reject_payroll")) target = "/admin/payroll-approvals";
    else if (perms.has("approve_payroll_roster")) target = "/admin/pay-roster-approvals";
    else if ([...perms].some((p) => p.startsWith("report_") || p === "workmen_roster_report")) target = "/reports";
    else if (perms.has("view_contractors")) target = "/directory";
    else if (perms.has("manage_admins") || perms.has("manage_masters") || perms.has("manage_roles")) target = "/admin/settings";
    return <Navigate to={target} replace />;
  }

  return (
    <div className="dash-dark px-4 py-6 md:px-6 lg:px-8">
      <MobileQuickNav user={user} />
      <DashboardHeader segment={segment} setSegment={setSegment} data={data} onRefresh={load} busy={busy} />

      <div className="mt-6 space-y-6">
          <DailyQuote />

          <KpiStrip data={data} segment={segment} />

          {/* Live Ready-to-Mobilize / Ready-to-Join inbound signals — polls every 25s */}
          <ReadySignalsBanner />

          <div className="grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3"><WorkforceMap data={data} segment={segment} /></div>
            <div className="lg:col-span-2"><StateDonut data={data} segment={segment} /></div>
          </div>

          {(data?.top_state_detail || data?.second_state_detail) && (
            <div className="grid gap-4 lg:grid-cols-2">
              {data?.top_state_detail && (
                <TopStateMap detail={data.top_state_detail} segment={segment} rank={1} />
              )}
              {data?.second_state_detail && (
                <TopStateMap detail={data.second_state_detail} segment={segment} rank={2} />
              )}
            </div>
          )}

          <CityCards data={data} />

          <div className="grid gap-4 lg:grid-cols-2">
            <TradeBars data={data} />
            <ReferralSource data={data} />
          </div>

          <MonthlyChart data={data} />

          <div className="grid gap-4 lg:grid-cols-4">
            <ContractorMatrix data={data} segment={segment} />
            <MobilizationHotspots data={data} />
            <WorkforceFunnel data={data} />
            <ReadyByState data={data} />
          </div>

          {/* Events (festivals, harvesting, LLM-driven events) and Weather
              were moved to Reports → Events · Weather in v1.7. The dashboard
              now focuses purely on execution KPIs to keep operators
              scanning quickly without vertical scroll. */}

          <MissingLocationPanel canEdit={(user?.permissions || []).includes("edit_contractors") || !!user?.is_super} />

          {/* Footer: deploy heartbeat badge — polls /api/health every 60s */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 pt-4 print:hidden">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              // NCC-SWMS Manpower Databank · Admin Console
            </div>
            <DeployHeartbeat />
          </div>
      </div>
    </div>
  );
}

// ============================================================
// HEADER
// ============================================================
function SiteScopePill() {
  // Reads the logged-in admin's site assignment to surface a "Viewing: …" pill
  // next to the dashboard title. Super-admins (or admins with no `sites` set)
  // see "Viewing: All sites" — everyone else sees the comma-separated list.
  const { user } = useAuth();
  if (!user) return null;
  const isSuper = !!user.is_super;
  const sites = Array.isArray(user.sites) ? user.sites : [];
  const scoped = !isSuper && sites.length > 0;
  const label = isSuper || sites.length === 0
    ? "All sites"
    : sites.slice(0, 3).join(" · ") + (sites.length > 3 ? ` +${sites.length - 3}` : "");
  return (
    <span
      data-testid="dashboard-site-scope-pill"
      title={isSuper ? "Super Admin — full visibility" : (sites.length === 0 ? "No site restriction on this account" : `Restricted to: ${sites.join(", ")}`)}
      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-0.5 font-mono text-[11px] ${
        scoped ? "border-blueprint bg-blueprint/10 text-blueprint" : "border-emerald-500 bg-emerald-50 text-emerald-700"
      }`}
    >
      <MapPin className="h-3 w-3" />
      <span className="industrial-label text-[10px]">Viewing:</span>
      <b>{label}</b>
    </span>
  );
}

function DashboardHeader({ segment, setSegment, data, onRefresh, busy }) {
  const generated = data?.generated_at ? new Date(data.generated_at) : new Date();
  const stamp = generated.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  const [isFullscreen, setIsFullscreen] = useState(typeof document !== "undefined" && !!document.fullscreenElement);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);
  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">NCC-SWMS Manpower Databank</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <div className="font-mono text-xs text-slate-500">Last updated: <b>{stamp}</b> · <span className="text-emerald-600">● Live</span></div>
          <SiteScopePill />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <div className="inline-flex border-2 border-slate-900 bg-white" data-testid="dashboard-segment-toggle">
          <button onClick={() => setSegment("all")} data-testid="dashboard-seg-all"
            title="Consolidated view — contractor + payroll combined"
            className={`px-4 py-2 industrial-label transition-colors ${segment === "all" ? "bg-emerald-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}>Cont+Pay</button>
          <button onClick={() => setSegment("contractor")} data-testid="dashboard-seg-contractor"
            className={`border-l-2 border-slate-900 px-4 py-2 industrial-label transition-colors ${segment === "contractor" ? "bg-ncc-gold text-ncc-navy" : "text-slate-700 hover:bg-slate-100"}`}>Contractor</button>
          <button onClick={() => setSegment("payroll")} data-testid="dashboard-seg-payroll"
            className={`border-l-2 border-slate-900 px-4 py-2 industrial-label transition-colors ${segment === "payroll" ? "bg-blueprint text-white" : "text-slate-700 hover:bg-slate-100"}`}>Payroll</button>
        </div>
        <button onClick={onRefresh} disabled={busy} data-testid="dashboard-refresh"
          className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:bg-slate-100 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Refresh
        </button>
        <button onClick={toggleFullscreen} data-testid="dashboard-fullscreen"
          title={isFullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
          className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:bg-slate-100">
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />} {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
        <PushToggle />
      </div>
    </div>
  );
}

// ============================================================
// PANEL helper
// ============================================================
function Panel({ title, icon, children, testid, accent = "navy", action }) {
  const headerCls = accent === "gold" ? "bg-ncc-gold text-ncc-navy" : "bg-ncc-navy text-white";
  return (
    <div className="flex h-full flex-col border-2 border-slate-900 bg-white" data-testid={testid}>
      <div className={`flex items-center gap-2 border-b-2 border-slate-900 px-3 py-2 industrial-label ${headerCls}`}>
        {icon}<span>{title}</span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="flex flex-1 flex-col p-3">{children}</div>
    </div>
  );
}

// ============================================================
// KPI STRIP
// ============================================================
function KpiStrip({ data, segment }) {
  const k = data?.kpis || {};
  const d = k.deltas || {};
  // Build a 2-line "Cont/Pay" subtitle showing Manpower and Records side-by-side.
  // Pass JSX so the card can render two compact lines instead of a single string.
  function splitLines(mpCont, mpPay, recCont, recPay) {
    return (
      <div className="font-mono text-[11px] leading-tight text-slate-600">
        <div><span className="text-slate-400">Mp</span> · CONT {mpCont} · PAY {mpPay}</div>
        <div><span className="text-slate-400">Rec</span> · CONT {recCont} · PAY {recPay}</div>
      </div>
    );
  }

  const items = [
    {
      label: "Total Workforce Capacity",
      value: k.total_workers,
      small: splitLines(
        k.total_workers_contractor ?? 0, k.total_workers_payroll ?? 0,
        k.total_records_contractor ?? 0, k.total_records_payroll ?? 0,
      ),
      delta: d.workers,
      icon: <Hammer className="h-5 w-5" />,
      accent: "bg-ncc-gold",
    },
    {
      label: "Current NCC Workforce",
      value: k.nccl_workforce ?? 0,
      small: splitLines(
        k.nccl_workforce_contractor ?? 0, k.nccl_workforce_payroll ?? 0,
        k.in_ncc_contractor ?? 0, k.in_ncc_payroll ?? 0,
      ),
      delta: d.in_ncc,
      icon: <Wrench className="h-5 w-5" />,
      accent: "bg-white",
    },
    {
      label: "New Registered",
      value: (k.new_registered_workforce_contractor ?? 0) + (k.new_registered_workforce_payroll ?? 0),
      small: splitLines(
        k.new_registered_workforce_contractor ?? 0, k.new_registered_workforce_payroll ?? 0,
        k.new_registered_contractor ?? 0, k.new_registered_payroll ?? 0,
      ),
      delta: d.new_registered,
      icon: <Users className="h-5 w-5" />,
      accent: "bg-white",
    },
    {
      label: "Workmen Roster",
      value: k.workmen_roster_approved ?? 0,
      small: (
        <div className="font-mono text-[11px] leading-tight text-slate-600">
          <div><span className="text-slate-400">Active</span> · CONT {k.workmen_roster_approved_contractor ?? 0} · PAY {k.workmen_roster_approved_payroll ?? 0}</div>
          <div><span className="text-slate-400">Queue</span> · Pending {k.workmen_roster_pending ?? 0} · Rejected {k.workmen_roster_rejected ?? 0}</div>
        </div>
      ),
      icon: <HardHat className="h-5 w-5" />,
      accent: "bg-white",
    },
    { label: "Villages Covered", value: k.villages_covered ?? 0, icon: <MapPin className="h-5 w-5" />, accent: "bg-white" },
    { label: "Cities Covered", value: k.cities_covered, delta: d.cities, icon: <Building2 className="h-5 w-5" />, accent: "bg-white" },
    { label: "States Covered", value: k.states_covered, delta: d.states, icon: <Map className="h-5 w-5" />, accent: "bg-white" },
    { label: "Largest State", value: k.largest_state?.state || "—", small: k.largest_state ? `${k.largest_state.workers} (${k.largest_state.share}%)` : "", icon: <Star className="h-5 w-5" />, accent: "bg-white" },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8" data-testid="kpi-strip">
      {items.map((it) => (
        <div key={it.label} className={`border-2 border-slate-900 px-3 py-3 transition-transform hover:-translate-y-0.5 hover:shadow-brutal-sm ${it.accent}`} data-testid={`kpi-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-2">
            <span className="text-ncc-navy">{it.icon}</span>
            <div className="industrial-label text-[10px] text-slate-700">{it.label}</div>
          </div>
          <div className="mt-1 font-heading text-2xl font-black leading-tight text-slate-900">{it.value ?? "—"}</div>
          {it.small && (typeof it.small === "string" ? (
            <div className="font-mono text-[11px] text-slate-600">{it.small}</div>
          ) : it.small)}
          {typeof it.delta === "number" && (
            <div className={`mt-0.5 font-mono text-[11px] ${it.delta > 0 ? "text-emerald-600" : it.delta < 0 ? "text-red-600" : "text-slate-500"}`}>
              {it.delta > 0 ? "▲" : it.delta < 0 ? "▼" : "—"} {Math.abs(it.delta)}% vs last month
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// 1. WORKFORCE MAP (India)
// ============================================================
function WorkforceMap({ data, segment }) {
  return (
    <Panel title="Workforce Availability Map (India)" icon={<Map className="h-4 w-4" />} testid="panel-map">
      <div className="relative">
        <IndiaChoropleth byState={data?.by_state || []} segment={segment} height={880} />
        {/* Floating legend — top-right corner of the map */}
        <div
          className="pointer-events-none absolute right-2 top-2 z-10 flex flex-col gap-1.5 rounded border border-slate-700/60 bg-slate-900/70 px-2.5 py-2 text-[11px] font-mono text-slate-200 backdrop-blur-sm"
          data-testid="india-map-legend"
        >
          <span className="flex items-center gap-2"><i className="inline-block h-3 w-3 bg-red-500" /> High (500+)</span>
          <span className="flex items-center gap-2"><i className="inline-block h-3 w-3 bg-amber-500" /> Medium (100–500)</span>
          <span className="flex items-center gap-2"><i className="inline-block h-3 w-3 bg-ncc-gold" /> Low (1–100)</span>
          <span className="flex items-center gap-2"><i className="inline-block h-3 w-3 bg-slate-300" /> No Data</span>
        </div>
      </div>
    </Panel>
  );
}

// ============================================================
// 2. STATE DONUT
// ============================================================
const STATE_COLORS = ["#ef4444", "#f59e0b", "#fbbf24", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4"];
function StateDonut({ data, segment }) {
  const by_state = data?.by_state || [];
  const top = by_state.slice(0, 5).map((s) => ({ name: s.state, value: s.workers, share: s.share }));
  const total = data?.kpis?.total_workers || 0;
  const isAll = segment === "all";
  const k = data?.kpis || {};
  const top1 = by_state[0];
  return (
    <Panel title="State Strength Distribution" icon={<TrendingUp className="h-4 w-4" />} testid="panel-state-donut">
      <div className="relative flex-1 min-h-[24rem]">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={top.length ? top : [{ name: "—", value: 1 }]} dataKey="value" innerRadius="62%" outerRadius="92%" startAngle={90} endAngle={-270} stroke="#0f172a" strokeWidth={1}>
              {(top.length ? top : [{}]).map((_, i) => <Cell key={i} fill={STATE_COLORS[i % STATE_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} workers`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-heading text-5xl font-black text-slate-900">{total}</div>
          <div className="industrial-label text-xs text-slate-500">Total Workers</div>
          {isAll && (
            <div className="mt-1 font-mono text-[11px] font-bold text-slate-700" data-testid="donut-split">
              CONT {k.total_workers_contractor ?? 0} · PAY {k.total_workers_payroll ?? 0}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 space-y-1 text-sm">
        {top.slice(0, 3).map((s, i) => (
          <div key={s.name} className="flex items-center gap-2 font-mono">
            <i className="inline-block h-3 w-3 rounded-full" style={{ background: STATE_COLORS[i] }} />
            <span className="font-heading font-bold text-slate-900">{s.name}</span>
            <span className="ml-auto text-slate-700">{s.value} ({s.share}%)</span>
          </div>
        ))}
        {top1 && (
          <div className="mt-2 border-l-4 border-ncc-gold bg-amber-50 px-2 py-1 font-mono text-[11px] text-amber-900">
            <b>{top1.share}%</b> of total workforce is from <b>{top1.state}</b>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ============================================================
// 3. TRADE BARS
// ============================================================
function TradeBars({ data }) {
  const top_trade = data?.kpis?.largest_trade;
  const trades = (data?.by_trade || []).slice(0, 5);
  const others = (data?.by_trade || []).slice(5);
  const rows = [...trades];
  if (others.length) {
    rows.push({ trade: `Others (${others.length} Trades)`, workers: others.reduce((a, t) => a + t.workers, 0), share: others.reduce((a, t) => a + t.share, 0) });
  }
  // Reverse so largest renders at top (Recharts plots bottom-up)
  const chartData = [...rows].reverse();
  return (
    <Panel title="Trade Strength Analysis" icon={<BarChart3 className="h-4 w-4" />} testid="panel-trade-bars">
      <div className="grid gap-4 lg:grid-cols-[2fr_180px]">
        <div>
          <div className="mb-1 grid grid-cols-[1fr_80px_80px] items-center gap-3 industrial-label text-[10px] text-slate-500">
            <span>Trade</span><span className="text-right">Workers</span><span className="text-right">% Share</span>
          </div>
          {rows.length === 0 && <div className="py-8 text-center font-mono text-sm text-slate-500">No trade data.</div>}
          {rows.map((t) => (
            <div key={t.trade} className="grid grid-cols-[1fr_80px_80px] items-center gap-3 text-sm">
              <div className="truncate font-heading text-sm font-bold text-slate-900">{t.trade}</div>
              <div className="text-right font-mono text-sm"><b>{t.workers}</b></div>
              <div className="text-right font-mono text-sm">{t.share}%</div>
            </div>
          ))}
          <div className="mt-3 h-44">
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ top: 2, right: 10, left: 4, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="trade" type="category" hide />
                <Tooltip />
                <Bar dataKey="workers" fill="#F7B500" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-2">
          <div className="border-2 border-slate-900 bg-ncc-tint p-3 text-center">
            <div className="industrial-label text-[10px] text-slate-500">Top Trade</div>
            <div className="mt-1 font-heading text-base font-bold leading-tight text-slate-900">{top_trade?.trade || "—"}</div>
            <div className="mt-1 font-mono text-xs text-slate-700">{top_trade ? `${top_trade.workers} · ${top_trade.share}%` : ""}</div>
          </div>
          <div className="border-2 border-slate-900 bg-white p-3 text-center">
            <div className="industrial-label text-[10px] text-slate-500">Total Trades</div>
            <div className="mt-1 font-heading text-3xl font-black text-slate-900">{data?.kpis?.active_trades ?? 0}</div>
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ============================================================
// 4B. REFERRAL SOURCE (Registration channel breakdown)
// ============================================================
const REFERRAL_COLORS = {
  "NCC Employee": "#22c55e",      // emerald
  "Agency": "#3b82f6",            // blue
  "Contractor": "#fbbf24",        // gold
  "Source / Others": "#a855f7",   // purple
};
function ReferralSource({ data }) {
  const rows = (data?.by_referral || []).map((r) => ({ ...r, color: REFERRAL_COLORS[r.source] || "#64748b" }));
  const total = rows.reduce((a, r) => a + (r.count || 0), 0);
  const hasAny = total > 0;
  const top = hasAny ? [...rows].sort((a, b) => b.count - a.count)[0] : null;
  // For zero-data, render a hollow donut with even slices for visual completeness.
  const chartData = hasAny ? rows : rows.map((r) => ({ ...r, count: 1 }));
  return (
    <Panel title="Referral Source" icon={<UserPlus className="h-4 w-4" />} testid="panel-referral-source">
      <div className="grid gap-4 lg:grid-cols-[2fr_180px]">
        <div>
          <div className="mb-1 grid grid-cols-[1fr_80px_80px] items-center gap-3 industrial-label text-[10px] text-slate-500">
            <span>Source</span><span className="text-right">Count</span><span className="text-right">% Share</span>
          </div>
          {rows.map((r) => (
            <div key={r.source} className="grid grid-cols-[1fr_80px_80px] items-center gap-3 text-sm" data-testid={`referral-row-${r.source.replace(/\s|\//g, "-")}`}>
              <div className="flex min-w-0 items-center gap-2 truncate font-heading text-sm font-bold text-slate-900">
                <i className="inline-block h-2.5 w-2.5 shrink-0" style={{ background: r.color }} />
                <span className="truncate">{r.source}</span>
              </div>
              <div className="text-right font-mono text-sm"><b>{r.count}</b></div>
              <div className="text-right font-mono text-sm">{r.share}%</div>
            </div>
          ))}
          {/* Donut chart — same vertical footprint as TradeBars (h-44) */}
          <div className="mt-3 h-44">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="source"
                  innerRadius="55%"
                  outerRadius="90%"
                  startAngle={90}
                  endAngle={-270}
                  stroke="#0f172a"
                  strokeWidth={1}
                  paddingAngle={hasAny ? 2 : 0}
                >
                  {chartData.map((r, i) => <Cell key={i} fill={hasAny ? r.color : "#cbd5e1"} />)}
                </Pie>
                {hasAny && <Tooltip formatter={(v, n) => [`${v} sign-ups`, n]} />}
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-2">
          <div className="border-2 border-slate-900 bg-ncc-tint p-3 text-center">
            <div className="industrial-label text-[10px] text-slate-500">Top Channel</div>
            <div className="mt-1 font-heading text-base font-bold leading-tight text-slate-900" data-testid="referral-top-channel">
              {top ? top.source : "—"}
            </div>
            <div className="mt-1 font-mono text-xs text-slate-700">
              {top ? `${top.count} · ${top.share}%` : "No referrals yet"}
            </div>
          </div>
          <div className="border-2 border-slate-900 bg-white p-3 text-center">
            <div className="industrial-label text-[10px] text-slate-500">Total Referred</div>
            <div className="mt-1 font-heading text-3xl font-black text-slate-900" data-testid="referral-total">{total}</div>
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ============================================================
// 5. CITY CARDS (top 5 with gauge)
// ============================================================
function CityCards({ data }) {
  const cities = (data?.by_city || []).slice(0, 5);
  const palette = ["#22c55e", "#f59e0b", "#3b82f6", "#a855f7", "#06b6d4"];
  if (cities.length === 0) return (
    <Panel title="Mobilization Cities" icon={<MapPin className="h-4 w-4" />} testid="panel-city-cards">
      <div className="py-8 text-center font-mono text-sm text-slate-500">No data.</div>
    </Panel>
  );
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="panel-city-cards">
      {cities.map((c, i) => {
        const color = palette[i % palette.length];
        const pct = Math.min(100, Math.round(c.share));
        const photo = imageForCity(c.city);
        return (
          <div key={c.city} className="group relative overflow-hidden border-2 border-slate-900 bg-slate-900 transition-transform hover:-translate-y-0.5 hover:shadow-brutal-sm" style={{ minHeight: 180 }}>
            <img src={photo} alt={c.city} className="absolute inset-0 h-full w-full object-cover opacity-60 transition-opacity group-hover:opacity-75" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/55 to-transparent" />
            <div className="relative z-10 flex h-full min-h-[180px] flex-col p-3">
              <div className="flex items-center gap-2 text-white">
                <Building2 className="h-4 w-4 text-ncc-gold" />
                <div className="industrial-label text-xs">{c.city}</div>
              </div>
              <div className="relative mt-2 flex flex-1 items-center justify-center">
                <svg viewBox="0 0 100 100" className="h-20 w-20 -rotate-90">
                  <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.18)" strokeWidth="10" fill="none" />
                  <circle cx="50" cy="50" r="40" stroke={color} strokeWidth="10" fill="none"
                    strokeDasharray={`${(pct / 100) * 251} 251`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-heading text-lg font-black text-white">{c.workers}</div>
                  <div className="font-mono text-[10px] text-white/80">{c.share}%</div>
                </div>
              </div>
              <div className={`text-center font-mono text-[11px] ${c.trend_delta_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {c.trend_delta_pct >= 0 ? "▲" : "▼"} {Math.abs(c.trend_delta_pct)}% mom
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// 7. CONTRACTOR MATRIX
// ============================================================
function ContractorMatrix({ data, segment }) {
  const rows = data?.top_contractors || [];
  const { can } = useAuth();
  const showDirectoryLink = can?.("report_directory");
  const label = segment === "payroll"
    ? "Payroll Availability Matrix"
    : segment === "all"
      ? "Workforce Availability Matrix"
      : "Contractor Availability Matrix";
  return (
    <Panel title={label} icon={<Users className="h-4 w-4" />} testid="panel-contractor-matrix">
      <table className="w-full text-sm">
        <thead><tr className="text-left industrial-label text-[10px] text-slate-500">
          <th className="py-1 pr-2">{segment === "payroll" ? "Worker" : "Contractor"}</th><th className="py-1 pr-2 text-right">Crew</th><th className="py-1 pr-2">Availability</th><th className="py-1 pr-2 text-right">Status</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-center font-mono text-slate-500">No data.</td></tr>}
          {rows.map((r) => {
            const color = r.availability >= 95 ? "bg-emerald-500" : r.availability >= 75 ? "bg-amber-500" : "bg-red-500";
            const dot = r.availability >= 95 ? "bg-emerald-500" : r.availability >= 75 ? "bg-amber-500" : "bg-red-500";
            return (
              <tr key={r.id} className="border-t border-slate-200">
                <td className="py-1.5 pr-2 font-heading font-bold text-slate-900">{r.name}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{r.crew}</td>
                <td className="py-1.5 pr-2">
                  <div className="flex items-center gap-2">
                    <div className="h-3 flex-1 border border-slate-300 bg-slate-50">
                      <div className={`h-full ${color}`} style={{ width: `${r.availability}%` }} />
                    </div>
                    <span className="font-mono text-xs">{r.availability}%</span>
                  </div>
                </td>
                <td className="py-1.5 pr-2 text-right">
                  <span className="inline-flex items-center gap-1 font-mono text-xs">
                    <i className={`inline-block h-2 w-2 rounded-full ${dot}`} /> {r.status_label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Link to="/directory" data-testid="matrix-view-all" className={`mt-3 inline-flex items-center gap-1 text-sm text-blueprint hover:underline ${showDirectoryLink ? "" : "hidden"}`}>
        View all {segment === "payroll" ? "payroll" : segment === "all" ? "records" : "contractors"} <ChevronRight className="h-3 w-3" />
      </Link>
    </Panel>
  );
}

// ============================================================
// 9. MOBILIZATION HOTSPOTS
// ============================================================
function MobilizationHotspots({ data }) {
  const rows = data?.hotspots || [];
  const max = Math.max(1, ...rows.map((r) => r.workers));
  return (
    <Panel title="Mobilization Source Hotspots" icon={<MapPin className="h-4 w-4" />} testid="panel-hotspots">
      <div className="space-y-2 text-sm">
        {rows.length === 0 && <div className="py-4 text-center font-mono text-slate-500">No data.</div>}
        {rows.map((h) => (
          <div key={h.city} className="grid grid-cols-[140px_1fr_70px] items-center gap-2">
            <div className="truncate font-heading text-xs font-bold text-slate-900">{h.city}{h.state ? `, ${h.state}` : ""}</div>
            <div className="relative h-3 border border-slate-300 bg-slate-50"><div className="h-full bg-blueprint" style={{ width: `${(h.workers / max) * 100}%` }} /></div>
            <div className="text-right font-mono text-xs"><b>{h.workers}</b> <span className="text-slate-500">{h.share}%</span></div>
          </div>
        ))}
      </div>
      <Link to="/reports" data-testid="hotspots-view-all" className="mt-3 inline-flex items-center gap-1 text-sm text-blueprint hover:underline">
        View all locations <ChevronRight className="h-3 w-3" />
      </Link>
    </Panel>
  );
}

// ============================================================
// 10. MONTHLY CHART
// ============================================================
function MonthlyChart({ data }) {
  const rows = data?.monthly || [];
  const ytd = data?.ytd || { workers: 0, contractors: 0 };
  return (
    <Panel title="Workforce Added (Monthly)" icon={<TrendingUp className="h-4 w-4" />} testid="panel-monthly">
      <div className="h-56">
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 6, right: 10, left: -10, bottom: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar yAxisId="l" dataKey="workers" fill="#fbbf24" stroke="#0f172a" strokeWidth={1} radius={[2, 2, 0, 0]} />
            <ReLine yAxisId="r" dataKey="contractors" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3, fill: "#1e3a8a" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1 text-xs font-mono text-slate-700">
        <div>Total Workers Added (YTD): <b>{ytd.workers}</b></div>
        <div>Total {data?.segment === "payroll" ? "Payroll" : data?.segment === "all" ? "Records" : "Contractors"} (YTD): <b>{ytd.contractors}</b></div>
      </div>
    </Panel>
  );
}

// ============================================================
// 11. FESTIVAL TRACKER
// ============================================================
function FestivalTracker({ data }) {
  const rows = data?.festivals || [];
  const allInWindow = rows.length > 0 && rows.every((f) => {
    const start = new Date(f.start);
    const diffDays = (start - new Date()) / (1000 * 60 * 60 * 24);
    return diffDays <= 60;
  });
  const title = allInWindow
    ? "Manpower Holiday & Festival Tracker (Next 60 Days)"
    : "Manpower Holiday & Festival Tracker (Upcoming)";
  return (
    <Panel title={title} icon={<Calendar className="h-4 w-4" />} testid="panel-festival" accent="gold">
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white"><tr className="text-left industrial-label text-[10px] text-slate-500">
            <th className="py-1 pr-2">State</th><th className="py-1 pr-2">Event</th><th className="py-1 pr-2">Impact</th><th className="py-1 pr-2">Start</th><th className="py-1 pr-2 text-right">Days</th><th className="py-1 pr-2 text-right">Est.</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center font-mono text-slate-500">No festivals — no workforce yet in festival-tracked states.</td></tr>}
            {rows.map((f) => (
              <tr key={f.state + f.event} className="border-t border-slate-200">
                <td className="py-1.5 pr-2 font-heading font-bold">{f.state}</td>
                <td className="py-1.5 pr-2">{f.event}</td>
                <td className="py-1.5 pr-2"><ImpactDot level={f.impact} /></td>
                <td className="py-1.5 pr-2 font-mono">{new Date(f.start).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{f.duration_days}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-red-600">{f.est_impact_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Link to="/reports" className="mt-2 inline-flex items-center gap-1 text-xs text-blueprint hover:underline">View full calendar <ChevronRight className="h-3 w-3" /></Link>
    </Panel>
  );
}

function ImpactDot({ level }) {
  const map = { High: "bg-red-500 text-red-700", Medium: "bg-amber-500 text-amber-700", Low: "bg-emerald-500 text-emerald-700" };
  const cls = map[level] || "bg-slate-400 text-slate-700";
  return <span className="inline-flex items-center gap-1"><i className={`inline-block h-2 w-2 rounded-full ${cls.split(" ")[0]}`} /><span className={`font-mono text-[11px] ${cls.split(" ")[1]}`}>{level}</span></span>;
}

// ============================================================
// 12. HARVESTING ALERT
// ============================================================
function HarvestingAlert({ data }) {
  const rows = data?.harvesting || [];
  return (
    <Panel title="Harvesting Period Alert" icon={<Wheat className="h-4 w-4" />} testid="panel-harvest" accent="gold">
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white"><tr className="text-left industrial-label text-[10px] text-slate-500">
            <th className="py-1 pr-2">State</th><th className="py-1 pr-2">Season</th><th className="py-1 pr-2">From-To</th><th className="py-1 pr-2">Status</th><th className="py-1 pr-2 text-right">Est.</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-center font-mono text-slate-500">No harvest seasons — no workforce yet in harvest-tracked states.</td></tr>}
            {rows.map((h) => (
              <tr key={h.state + h.season} className="border-t border-slate-200">
                <td className="py-1.5 pr-2 font-heading font-bold">{h.state}</td>
                <td className="py-1.5 pr-2">{h.season}</td>
                <td className="py-1.5 pr-2 font-mono text-[11px]">{h.from_month} – {h.to_month}</td>
                <td className="py-1.5 pr-2">
                  {h.active ? <span className="inline-flex items-center gap-1 font-mono text-[11px] text-red-700"><Star className="h-3 w-3 fill-red-600 text-red-600" /> Active</span>
                    : <span className="inline-flex items-center gap-1 font-mono text-[11px] text-amber-700"><Clock className="h-3 w-3" /> Upcoming</span>}
                </td>
                <td className="py-1.5 pr-2 text-right font-mono text-red-600">{h.est_impact_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 border-l-4 border-amber-500 bg-amber-50 px-2 py-1 font-mono text-[11px] text-amber-900">
        <AlertTriangle className="inline h-3 w-3" /> Note: Estimated manpower reduction during active harvesting period.
      </div>
    </Panel>
  );
}

// ============================================================
// 12B. UPCOMING EVENTS (Elections + Emergency) — AI-generated, hourly cache
// ============================================================
function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [source, setSource] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  async function load(force = false) {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get("/dashboard/upcoming-events" + (force ? "?force=1" : ""));
      setEvents(r.data?.events || []);
      setGeneratedAt(r.data?.generated_at || null);
      setSource(r.data?.source || null);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 60 * 60 * 1000); // hourly refresh
    return () => clearInterval(t);
  }, []);
  const isLive = source === "claude-sonnet-4-5";
  return (
    <Panel
      title="Upcoming Events · Elections & Emergencies"
      icon={<AlertCircle className="h-4 w-4" />}
      testid="panel-upcoming-events"
      accent="gold"
      action={
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="upcoming-events-refresh"
          className="inline-flex items-center gap-1 border border-ncc-navy/30 bg-white/20 px-2 py-0.5 text-[10px] font-bold tracking-wider transition-colors hover:bg-white/40 disabled:opacity-60"
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <span>↻</span>}
          Refresh
        </button>
      }
    >
      <div className="mb-2 flex items-center justify-between font-mono text-[10px]">
        <span className={isLive ? "text-emerald-700" : "text-amber-700"} data-testid="upcoming-events-source">
          <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${isLive ? "bg-emerald-500" : "bg-amber-500"}`} />
          {isLive ? "Claude Sonnet 4.5 · Live · refreshes hourly (IST)" : "Fallback list · Click Refresh to retry LLM"}
        </span>
      </div>
      <div className="max-h-72 overflow-auto">
        {loading ? (
          <div className="py-4 text-center font-mono text-xs text-slate-500">Loading…</div>
        ) : events.length === 0 ? (
          <div className="py-4 text-center font-mono text-xs text-slate-500">No upcoming events available.</div>
        ) : (
          <ul className="space-y-2">
            {events.map((e, i) => {
              const isElection = e.kind === "election";
              const impactColor = e.impact === "high" ? "text-red-700 bg-red-50 border-red-300"
                : e.impact === "medium" ? "text-amber-800 bg-amber-50 border-amber-300"
                : "text-emerald-700 bg-emerald-50 border-emerald-300";
              return (
                <li key={i} className="border-l-4 border-ncc-gold bg-slate-50 px-2 py-1.5" data-testid={`upcoming-event-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading text-[13px] font-bold text-ncc-navy">
                      {isElection ? "🗳" : "⚠"} {e.title}
                    </div>
                    <span className={`shrink-0 border px-1.5 py-0.5 industrial-label text-[9px] ${impactColor}`}>{e.impact}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 font-mono text-[11px] text-slate-600">
                    <span className="border border-slate-300 bg-white px-1.5 py-0.5">{e.state}</span>
                    <span>{e.date_range}</span>
                  </div>
                  {e.summary && <div className="mt-1 text-[11px] text-slate-700">{e.summary}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between font-mono text-[10px] text-slate-500">
        <span>{events.length} entries</span>
        {generatedAt && <span>{new Date(generatedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST</span>}
      </div>
    </Panel>
  );
}

// ============================================================
// 13. READY BY STATE
// ============================================================
function ReadyByState({ data }) {
  const rows = data?.ready_by_state || [];
  const overall = data?.overall_availability || 0;
  const totalReady = rows.reduce((a, r) => a + (r.ready || 0), 0);
  return (
    <Panel title="Ready to Mobilize (By State)" icon={<Send className="h-4 w-4" />} testid="panel-ready-by-state" accent="gold">
      <table className="w-full text-xs">
        <thead><tr className="text-left industrial-label text-[10px] text-slate-500">
          <th className="py-1 pr-2">State</th>
          <th className="py-1 pr-2 text-right" title="Strength (A) — total approved workers per Directory">Strength (A)</th>
          <th className="py-1 pr-2 text-right" title="Ready (B) — Nos as per Ready to Mobilize — full list">Ready (B)</th>
          <th className="py-1 pr-2 text-center" title="Availability = (B ÷ A)">B/A</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-center font-mono text-slate-500">No ready signals.</td></tr>}
          {rows.map((r) => {
            const pct = Math.max(0, Math.min(100, r.availability || 0));
            const color = pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444";
            return (
              <tr key={r.state} className="border-t border-slate-200">
                <td className="py-1.5 pr-2 font-heading font-bold">{r.state}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{r.strength ?? 0}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-emerald-600">{r.ready ?? 0}</td>
                <td className="py-1.5 pr-2">
                  <div className="flex items-center justify-center gap-1">
                    <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
                      <circle cx="18" cy="18" r="14" stroke="#e5e7eb" strokeWidth="3.5" fill="none" />
                      <circle cx="18" cy="18" r="14" stroke={color} strokeWidth="3.5" fill="none"
                        strokeDasharray={`${(pct / 100) * 88} 88`} strokeLinecap="round" />
                    </svg>
                    <span className="font-mono text-[11px]" style={{ color }}>{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3 text-sm">
        <div>
          <div className="font-mono text-[11px] text-slate-500">Ready to Mobilize</div>
          <div className="font-heading text-lg font-black text-emerald-600">● {totalReady}</div>
        </div>
        <div>
          <div className="font-mono text-[11px] text-slate-500">Overall Availability</div>
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
              <circle cx="18" cy="18" r="14" stroke="#e5e7eb" strokeWidth="3.5" fill="none" />
              <circle cx="18" cy="18" r="14" stroke="#22c55e" strokeWidth="3.5" fill="none"
                strokeDasharray={`${(Math.min(100, overall) / 100) * 88} 88`} strokeLinecap="round" />
            </svg>
            <span className="font-heading text-lg font-black text-emerald-600">{overall}%</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ============================================================
// 15. WORKFORCE FUNNEL
// ============================================================
function WorkforceFunnel({ data }) {
  const f = data?.funnel || { registered: 0, approved: 0, pending: 0, rejected: 0 };
  const stages = [
    { label: "Registered", value: f.registered, color: "#3b82f6" },
    { label: "Approved",   value: f.approved,   color: "#22c55e" },
    { label: "Pending",    value: f.pending,    color: "#f59e0b" },
    { label: "Rejected",   value: f.rejected,   color: "#ef4444" },
  ];
  // Funnel widths shrink from 100→40
  const widths = [100, 80, 60, 40];
  return (
    <Panel title="Workforce Funnel" icon={<TrendingDown className="h-4 w-4" />} testid="panel-funnel">
      <div className="grid grid-cols-[1fr_70px] items-center gap-2">
        <div className="relative">
          <svg viewBox="0 0 200 140" className="h-32 w-full" preserveAspectRatio="none">
            {stages.map((s, i) => {
              const top_w = widths[i];
              const bot_w = i < widths.length - 1 ? widths[i + 1] : 30;
              const y = i * 34 + 4;
              const x1 = (200 - top_w) / 2 * (top_w / 100);
              const x2 = 200 - x1;
              const x3 = 200 - (200 - bot_w) / 2 * (bot_w / 100);
              const x4 = (200 - bot_w) / 2 * (bot_w / 100);
              // Use simple trapezoid points
              const tl = (200 - top_w) / 2;
              const tr = tl + top_w;
              const bl = (200 - bot_w) / 2;
              const br = bl + bot_w;
              return (
                <g key={s.label}>
                  <polygon points={`${tl},${y} ${tr},${y} ${br},${y + 30} ${bl},${y + 30}`} fill={s.color} stroke="#0b1426" strokeWidth="0.6" opacity="0.95" />
                  <text x="100" y={y + 19} textAnchor="middle" fontFamily="ui-sans-serif,system-ui" fontSize="11" fontWeight="800" fill="#fff">{s.value}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div className="space-y-3">
          {stages.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <i className="inline-block h-2 w-2 rounded-full" style={{ background: s.color }} />
              <div className="industrial-label text-[10px] text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}


export function Metric({ icon, label, value, accent, testid, onClick, active }) {
  const isClickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      className={`group block w-full text-left border-2 border-slate-900 ${accent} p-5 shadow-brutal-sm transition-all ${isClickable ? "cursor-pointer hover:-translate-y-1 hover:shadow-brutal" : ""} ${active ? "-translate-y-1 shadow-brutal" : ""} focus:outline-none focus-visible:ring-2 focus-visible:ring-ncc-gold focus-visible:ring-offset-2`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="industrial-label">{label}</span>
        {icon}
      </div>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-mono text-3xl font-bold">{value}</span>
        {isClickable && (
          <span className="industrial-label opacity-50 transition-opacity group-hover:opacity-100">View →</span>
        )}
      </div>
    </button>
  );
}

export function StatusBadge({ s }) {
  const map = {
    approved: "bg-ncc-gold text-slate-900",
    pending: "bg-slate-100 text-slate-900",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`border border-slate-900 px-2 py-0.5 industrial-label ${map[s] || ""}`}>{s}</span>;
}

export function EditContractorModal({ contractor, onClose, onSaved }) {
  function computeAgeYears(dob) {
    if (!dob) return "";
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return "";
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
    return a >= 0 && a < 130 ? String(a) : "";
  }
  // Initialise trade_counts from contractor; fall back to crew distribution across trades
  function initTradeCounts() {
    if (contractor.trade_counts && Object.keys(contractor.trade_counts).length > 0) {
      return { ...contractor.trade_counts };
    }
    const ts = contractor.trades || [];
    if (ts.length === 0) return {};
    const crew = Number(contractor.crew_size || 0);
    if (crew >= ts.length) {
      const base = Math.floor(crew / ts.length);
      const rem = crew - base * ts.length;
      const out = {};
      ts.forEach((t, i) => { out[t] = base + (i === 0 ? rem : 0); });
      return out;
    }
    return Object.fromEntries(ts.map((t) => [t, 1]));
  }
  const isPayroll = !!contractor.is_payroll;
  const [form, setForm] = useState({
    name: contractor.name || "",
    contact_phone: contractor.contact_phone || "",
    contact_email: contractor.contact_email || "",
    location: contractor.location || "",
    contractor_type: contractor.contractor_type || "Labour Contractor",
    current_status: contractor.current_status || "NEW",
    ncc_site_name: contractor.ncc_site_name || "",
    id_proof_type: contractor.id_proof_type || (isPayroll ? "Aadhar" : "PAN"),
    id_proof_number: contractor.id_proof_number || "",
    bio: contractor.bio || "",
    trade_counts: initTradeCounts(),
    experience_years: contractor.experience_years || 0,
    photo: contractor.photo || null,
    id_docs: contractor.id_docs || [],
    certifications: contractor.certifications || [],
    projects: contractor.projects || [],
    referred_by: contractor.referred_by || "",
    referred_by_name: contractor.referred_by_name || "",
    referred_by_phone: contractor.referred_by_phone || "",
    date_of_birth: contractor.date_of_birth || "",
  });
  const [allTrades, setAllTrades] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [allIdTypes, setAllIdTypes] = useState(["PAN", "Aadhar"]);
  const [allSites, setAllSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [workmenOpen, setWorkmenOpen] = useState(false);
  const [workmen, setWorkmen] = useState([]);
  const [siteMasters, setSiteMasters] = useState([]);
  const [editingWorkmanId, setEditingWorkmanId] = useState(null);
  const workmenCount = workmen.length;

  const selectedTrades = Object.keys(form.trade_counts);
  const crewSize = Object.values(form.trade_counts).reduce((a, b) => a + (Number(b) || 0), 0);
  const blankTrades = selectedTrades.filter((t) => !form.trade_counts[t] || Number(form.trade_counts[t]) < 1);

  useEffect(() => {
    api.get("/meta/trades").then((r) => {
      setAllTrades(r.data.trades || []);
      setAllTypes(r.data.contractor_types || []);
      const ipt = r.data.id_proof_types || [];
      if (ipt.length > 0) setAllIdTypes(ipt);
      setAllSites(r.data.ncc_sites || []);
    }).catch(() => {});
    if (!isPayroll) {
      api.get(`/contractors/${contractor.id}/workmen`).then((r) => {
        setWorkmen(r.data || []);
      }).catch(() => {});
      api.get(`/masters/ncc_site`).then((r) => {
        setSiteMasters(r.data || []);
      }).catch(() => {});
    }
  }, [contractor.id, isPayroll]);

  function toggleTrade(t) {
    setForm((f) => {
      const next = { ...f.trade_counts };
      if (t in next) delete next[t]; else next[t] = "";
      return { ...f, trade_counts: next };
    });
  }

  function setTradeCount(t, n) {
    if (n === "" || n === null || n === undefined) {
      setForm((f) => ({ ...f, trade_counts: { ...f.trade_counts, [t]: "" } }));
      return;
    }
    const num = Math.max(1, Number(n) || 1);
    setForm((f) => ({ ...f, trade_counts: { ...f.trade_counts, [t]: num } }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (selectedTrades.length === 0) { setError("Select at least one trade"); return; }
    if (blankTrades.length > 0) { setError(`Enter Nos. for: ${blankTrades.join(", ")}`); return; }
    if (form.current_status === "NCCL" && !form.ncc_site_name.trim()) { setError("Enter the NCC Site Name"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        contact_email: form.contact_email || null,
        trades: selectedTrades,
        trade_counts: Object.fromEntries(selectedTrades.map((t) => [t, Number(form.trade_counts[t])])),
        crew_size: crewSize,
        experience_years: Number(form.experience_years),
        // Preserve segment flag & related fields the modal doesn't expose, so a Payroll
        // record doesn't silently flip to Contractor when admin saves an edit.
        is_payroll: !!contractor.is_payroll,
        employee_id: contractor.employee_id || null,
        experience_india_years: contractor.experience_india_years || 0,
        experience_abroad_years: contractor.experience_abroad_years || 0,
        contractor_type: isPayroll ? "Payroll Employee" : (form.contractor_type || "Labour Contractor"),
        referred_by: form.referred_by || null,
        referred_by_name: form.referred_by ? (form.referred_by_name || null) : null,
        referred_by_phone: form.referred_by ? (form.referred_by_phone || null) : null,
        date_of_birth: isPayroll ? (form.date_of_birth || null) : (contractor.date_of_birth || null),
        age_years: isPayroll && form.date_of_birth ? (Number(computeAgeYears(form.date_of_birth)) || null) : (contractor.age_years || null),
      };
      await api.put(`/contractors/${contractor.id}`, body);
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-ncc-navy/70 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-3xl border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="edit-contractor-form">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-5 py-3 text-white">
          <div className="industrial-label">Edit {isPayroll ? "Payroll Manpower" : "Contractor"} · {contractor.name}</div>
          <button type="button" onClick={onClose} data-testid="edit-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-5 p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <EField label="Full Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
            <EField label="Phone" required value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="edit-phone" />
            <EField label="Email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="edit-email" />
            <EField label="Location" required value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="edit-location" />
            {!isPayroll && (
              <ESelect label="Contractor Type" value={form.contractor_type} onChange={(v) => setForm({ ...form, contractor_type: v })} options={allTypes} testid="edit-type" />
            )}
            <ESelect label="Current Status" value={form.current_status} onChange={(v) => setForm({ ...form, current_status: v, ncc_site_name: v === "NEW" ? "" : form.ncc_site_name })} options={["NCCL", "NEW"]} testid="edit-status" />
            {form.current_status === "NCCL" && (
              <div className="block">
                <ELabel>NCC Site Name <span className="text-ncc-gold2">*</span></ELabel>
                <input
                  required
                  list="edit-ncc-site-list"
                  value={form.ncc_site_name}
                  onChange={(e) => setForm({ ...form, ncc_site_name: e.target.value })}
                  placeholder={allSites.length > 0 ? "Select or type a site…" : "NCC site"}
                  data-testid="edit-site"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
                <datalist id="edit-ncc-site-list">
                  {allSites.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 md:col-span-2">
              <ESelect label="ID Type" value={form.id_proof_type || allIdTypes[0]} onChange={(v) => setForm({ ...form, id_proof_type: v })} options={allIdTypes} testid="edit-id-type" />
              <div className="col-span-2">
                <EField label="ID Number" value={form.id_proof_number || ""} onChange={(v) => setForm({ ...form, id_proof_number: v.toUpperCase() })} testid="edit-id-number" />
              </div>
            </div>
            {isPayroll && (
              <>
                <div>
                  <ELabel>Date of Birth</ELabel>
                  <input
                    type="date"
                    value={form.date_of_birth || ""}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                    data-testid="edit-dob"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <ELabel>Age (Yrs)</ELabel>
                  <input
                    type="text"
                    readOnly
                    value={computeAgeYears(form.date_of_birth)}
                    data-testid="edit-age"
                    placeholder="Auto-calculated"
                    className="w-full cursor-not-allowed border-2 border-slate-200 bg-slate-100 px-3 py-2 font-mono text-sm text-slate-700"
                  />
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <ELabel>Referred By <span className="font-mono text-xs text-slate-500">(optional)</span></ELabel>
              <select
                value={form.referred_by || ""}
                onChange={(e) => setForm({ ...form, referred_by: e.target.value, referred_by_name: e.target.value ? form.referred_by_name : "", referred_by_phone: e.target.value ? form.referred_by_phone : "" })}
                data-testid="edit-referred-by"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              >
                <option value="">— None —</option>
                <option value="NCC Employee">NCC Employee</option>
                <option value="Agency">Agency</option>
                <option value="Contractor">Contractor</option>
                <option value="Source / Others">Source / Others</option>
              </select>
              {form.referred_by && (
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <EField label="Referrer Name" value={form.referred_by_name || ""} onChange={(v) => setForm({ ...form, referred_by_name: v })} testid="edit-referred-by-name" />
                  <EField label="Referrer Number" value={form.referred_by_phone || ""} onChange={(v) => setForm({ ...form, referred_by_phone: v })} testid="edit-referred-by-phone" />
                </div>
              )}
            </div>
          </div>

          <div>
            <ELabel>Trades <span className="font-mono text-xs text-slate-500">(click to toggle · enter Nos.)</span></ELabel>
            {(() => {
              const orphanTrades = Object.keys(form.trade_counts).filter((t) => !allTrades.includes(t));
              return (
                <>
                  {orphanTrades.length > 0 && (
                    <div data-testid="edit-trade-orphan-warn" className="mb-2 border-l-4 border-ncc-gold bg-ncc-tint px-3 py-2 text-xs text-slate-700">
                      <span className="industrial-label text-ncc-gold2">Heads up:</span>{" "}
                      <span className="font-mono">{orphanTrades.join(", ")}</span> {orphanTrades.length === 1 ? "is" : "are"} no longer in the active Trades master (likely renamed). Click to keep or untoggle to remove.
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {[...allTrades, ...orphanTrades].map((t) => {
                      const on = t in form.trade_counts;
                      const isOrphan = orphanTrades.includes(t);
                      return (
                        <div
                          key={t}
                          className={`flex items-center border-2 transition-all ${on ? (isOrphan ? "border-ncc-gold2 bg-safety-200 -translate-y-0.5 shadow-brutal-sm" : "border-slate-900 bg-ncc-gold -translate-y-0.5 shadow-brutal-sm") : "border-slate-300 bg-white hover:border-slate-900"}`}
                          title={isOrphan ? `${t} (renamed/removed from masters)` : undefined}
                        >
                          <button
                            type="button"
                            onClick={() => toggleTrade(t)}
                            data-testid={`edit-trade-${t}`}
                            className="px-2.5 py-1 industrial-label text-slate-900"
                          >
                            {t}{isOrphan ? " *" : ""}
                          </button>
                          {on && (
                            <input
                              type="number"
                              min="1"
                              value={form.trade_counts[t] ?? ""}
                              onChange={(e) => setTradeCount(t, e.target.value)}
                              placeholder="—"
                              data-testid={`edit-trade-count-${t}`}
                              className="w-14 border-l-2 border-slate-900 bg-white px-2 py-1 font-mono text-xs focus:bg-slate-50 focus:outline-none"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <ELabel>Crew Size <span className="font-mono text-xs text-slate-500">(auto)</span></ELabel>
              <div
                data-testid="edit-crew-size"
                className="flex items-baseline gap-3 border-2 border-slate-300 bg-slate-100 px-3 py-2"
              >
                <span className="font-heading text-xl font-black text-slate-900">{crewSize}</span>
                <span className="industrial-label text-slate-500">workers</span>
              </div>
            </div>
            <EField label="Experience (yrs)" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} testid="edit-exp" />
          </div>

          {!isPayroll && (
            <>
              <div className="flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-ncc-navy bg-ncc-tint px-4 py-3" data-testid="edit-workmen-widget">
                <div className="min-w-0">
                  <div className="industrial-label text-[10px] text-ncc-navy">Workmen Roster</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-700">
                    Registered: <b className="text-slate-900">{workmenCount}</b>
                    {" · "}Balance (Crew − Registered):{" "}
                    <b className={`${crewSize - workmenCount > 0 ? "text-amber-700" : crewSize - workmenCount < 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {crewSize - workmenCount}
                    </b>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setEditingWorkmanId(null); setWorkmenOpen(true); }}
                  data-testid="edit-workmen-btn"
                  className="inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm"
                >
                  <UserPlus className="h-4 w-4" /> Manage Workmen
                </button>
              </div>

              {/* Inline registered-workmen list (image 2) */}
              <WorkmenList
                workmen={workmen}
                contractor={contractor}
                siteMasters={siteMasters}
                onEdit={(w) => { setEditingWorkmanId(w.id); setWorkmenOpen(true); }}
                onDelete={async (w) => {
                  if (!window.confirm(`Delete workman "${w.name}"? This cannot be undone.`)) return;
                  try {
                    await api.delete(`/workmen/${w.id}`);
                    const r = await api.get(`/contractors/${contractor.id}/workmen`);
                    setWorkmen(r.data || []);
                  } catch (err) {
                    alert(err?.response?.data?.detail || "Delete failed");
                  }
                }}
                emptyHint="No workmen registered yet. Click ‘Manage Workmen’ to add the first one."
              />
            </>
          )}

          <div>
            <ELabel>About / Bio</ELabel>
            <textarea
              rows={3}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="edit-bio"
              className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
            />
          </div>

          <div className="border-t-2 border-dashed border-slate-300 pt-5">
            <div className="industrial-label mb-3 text-slate-700">Photo &amp; Documents</div>
            <div className="grid gap-5 md:grid-cols-3">
              <div data-testid="edit-photo-slot">
                <ELabel>Profile Photo</ELabel>
                <FileUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} accept="image/*" label="Replace photo" />
              </div>
              <div data-testid="edit-iddocs-slot">
                <ELabel>ID / License Docs</ELabel>
                <FileUpload value={form.id_docs} onChange={(v) => setForm({ ...form, id_docs: v })} multiple label="Add docs" />
              </div>
              <div data-testid="edit-certs-slot">
                <ELabel>Certifications</ELabel>
                <FileUpload value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} multiple label="Add certs" />
              </div>
            </div>
            <div className="mt-2 font-mono text-xs text-slate-500">
              Existing files are shown above — click the × on a file to remove it, or drop new files to add more.
            </div>
          </div>

          {error && <div className="border-2 border-red-500 bg-red-50 p-2 font-mono text-xs text-red-700" data-testid="edit-error">{error}</div>}
        </div>
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t-2 border-slate-900 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={async () => {
              const pw = window.prompt(`Reset login password for "${contractor.name}".\n\nEnter a new password (min 6 characters). The contractor will use their registered mobile + this password to log in.`);
              if (!pw) return;
              if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
              try {
                const r = await api.post(`/contractors/${contractor.id}/admin-reset-password`, { new_password: pw });
                toast.success(`Password reset · share with ${r.data.mobile}`, { duration: 8000 });
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Reset failed");
              }
            }}
            data-testid="edit-reset-pw-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:bg-slate-100"
          >
            <KeyRound className="h-4 w-4" />
            Reset Password
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label">Cancel</button>
            <button type="submit" disabled={saving} data-testid="edit-save-btn" className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {saving ? "Saving" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
      {!isPayroll && (
        <WorkmenDrawer
          open={workmenOpen}
          onClose={() => { setWorkmenOpen(false); setEditingWorkmanId(null); }}
          mode="admin"
          contractorId={contractor.id}
          contractorName={contractor.name}
          crewSize={crewSize}
          authToken={localStorage.getItem("ccdb_token")}
          editingWorkmanId={editingWorkmanId}
          onChanged={async () => {
            try {
              const r = await api.get(`/contractors/${contractor.id}/workmen`);
              setWorkmen(r.data || []);
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

function ELabel({ children }) { return <div className="industrial-label mb-1 text-slate-600">{children}</div>; }
function EField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <ELabel>{label}{required && <span className="text-ncc-gold2"> *</span>}</ELabel>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
    </label>
  );
}
function ESelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <ELabel>{label}</ELabel>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}


function SegmentSummary({ title, seg, accent, testidPrefix }) {
  const total = seg?.total || 0;
  const totalCrew = seg?.total_crew || 0;
  const barCls = accent === "ncc-gold" ? "bg-ncc-gold" : "bg-blueprint";
  return (
    <div className="mt-8" data-testid={`${testidPrefix}-summary-block`}>
      <div className="flex items-center justify-between border-b-2 border-slate-900 pb-2">
        <h3 className="font-heading text-lg font-extrabold tracking-tight text-slate-900">{title}</h3>
        <div className="font-mono text-xs text-slate-600">
          <span className="industrial-label text-slate-500">Records</span>{" "}
          <span className="font-bold text-slate-900">{total}</span>
          <span className="mx-2 text-slate-300">|</span>
          <span className="industrial-label text-slate-500">Workers</span>{" "}
          <span className="font-bold text-slate-900">{totalCrew}</span>
        </div>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <BreakdownTable
          icon={<Hammer className="h-4 w-4 text-ncc-gold" />}
          heading="Trade Wise"
          rows={seg?.by_trade || []}
          keyField="trade"
          extraCols={[{ label: "Workers", get: (r) => r.total_crew }]}
          barCls={barCls}
          testid={`${testidPrefix}-trade-wise`}
        />
        <BreakdownTable
          icon={<MapPin className="h-4 w-4 text-ncc-gold" />}
          heading="City Wise"
          rows={seg?.by_city || []}
          keyField="city"
          extraCols={[
            { label: "Workers", get: (r) => r.total_crew },
            { label: "Trades", get: (r) => r.trade_count },
          ]}
          barCls={barCls}
          testid={`${testidPrefix}-city-wise`}
        />
        <BreakdownTable
          icon={<MapPin className="h-4 w-4 text-ncc-gold" />}
          heading="State Wise"
          rows={seg?.by_state || []}
          keyField="state"
          extraCols={[
            { label: "Workers", get: (r) => r.total_crew },
            { label: "Trades", get: (r) => r.trade_count },
          ]}
          barCls={barCls}
          testid={`${testidPrefix}-state-wise`}
        />
      </div>
    </div>
  );
}

function BreakdownTable({ icon, heading, rows, keyField, extraCols, barCls, testid }) {
  const max = rows.length > 0 ? Math.max(...rows.map((r) => r.count)) : 0;
  const headers = [keyField.charAt(0).toUpperCase() + keyField.slice(1), "Count", ...extraCols.map((c) => c.label)];
  const totals = extraCols.map((c) => rows.reduce((s, r) => s + (Number(c.get(r)) || 0), 0));
  const totalCount = rows.reduce((s, r) => s + (Number(r.count) || 0), 0);
  return (
    <div className="border-2 border-slate-900 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 border-b-2 border-slate-900 bg-ncc-navy px-3 py-2 text-white">
        {icon}
        <div className="industrial-label">{heading}</div>
        <div className="ml-auto font-mono text-xs text-slate-300">{rows.length}</div>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-100">
            <tr className="border-b-2 border-slate-900">
              {headers.map((h) => (
                <th key={h} className="px-3 py-2 text-left industrial-label text-slate-700">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="p-4 text-center font-mono text-slate-500">No data</td></tr>
            ) : rows.map((r) => {
              const pct = max > 0 ? (r.count / max) * 100 : 0;
              return (
                <tr key={r[keyField]} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`${testid}-row-${r[keyField]}`}>
                  <td className="px-3 py-2">
                    <div className="font-heading font-bold text-slate-900">{r[keyField]}</div>
                    <div className="mt-1 h-1.5 border border-slate-900 bg-slate-100">
                      <div className={`h-full ${barCls}`} style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono font-bold">{r.count}</td>
                  {extraCols.map((c) => (
                    <td key={c.label} className="px-3 py-2 font-mono">{c.get(r)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-900 bg-slate-50" data-testid={`${testid}-totals-row`}>
                <td className="px-3 py-2 industrial-label text-slate-900">Total</td>
                <td className="px-3 py-2 font-mono font-extrabold text-slate-900">{totalCount}</td>
                {totals.map((t, i) => (
                  <td key={i} className="px-3 py-2 font-mono font-extrabold text-slate-900">{t}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function MissingLocationPanel({ canEdit = true }) {
  const [rows, setRows] = useState(null); // null = loading, [] = none, list = has data
  const [drafts, setDrafts] = useState({}); // {id: {city, state}}
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/contractors/missing-location");
        if (cancelled) return;
        setRows(r.data || []);
        // Pre-seed drafts with current values so admin can tweak before saving
        const next = {};
        (r.data || []).forEach((c) => { next[c.id] = { city: c.city || "", state: c.state || "" }; });
        setDrafts(next);
      } catch (err) {
        if (cancelled) return;
        // Permission denied → silently no-op (users without edit_contractors shouldn't see the panel anyway)
        if (err?.response?.status === 403) { setRows([]); return; }
        toast.error(err?.response?.data?.detail || "Failed to scan for missing locations");
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canEdit]);

  if (!canEdit) return null;

  async function save(id) {
    const d = drafts[id] || {};
    if (!d.city?.trim() || !d.state?.trim()) {
      toast.error("Both City and State are required");
      return;
    }
    setBusy(id);
    try {
      await api.patch(`/contractors/${id}/location`, { city: d.city.trim(), state: d.state.trim() });
      toast.success("Location updated");
      setRows((rs) => (rs || []).filter((r) => r.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  }

  if (rows === null || rows.length === 0) return null;

  return (
    <div className="mt-8 border-2 border-amber-600 bg-amber-50 shadow-brutal-sm print:hidden" data-testid="missing-location-panel">
      <div className="flex items-center gap-2 border-b-2 border-amber-600 bg-amber-100 px-4 py-3">
        <AlertTriangle className="h-5 w-5 text-amber-700" />
        <div className="industrial-label text-amber-800">Records with missing City / State · {rows.length} found</div>
      </div>
      <div className="px-4 pt-3 pb-1 font-mono text-xs text-amber-800">
        These contractors show up as <strong>&quot;Unknown&quot;</strong> in the State Wise summary above. Fill in City + State below and save to clear them.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-amber-100">
            <tr className="border-b-2 border-amber-600">
              {["Type", "Name", "Phone", "Current Location", "City *", "State *", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left industrial-label text-amber-900">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const d = drafts[c.id] || { city: "", state: "" };
              const isBusy = busy === c.id;
              return (
                <tr key={c.id} className="border-b border-amber-200" data-testid={`missing-loc-row-${c.id}`}>
                  <td className="px-3 py-2">
                    {c.is_payroll ? (
                      <span className="inline-flex items-center border-2 border-slate-900 bg-blueprint px-2 py-0.5 text-[10px] font-bold tracking-wider text-white">PAYROLL</span>
                    ) : (
                      <span className="inline-flex items-center border-2 border-slate-900 bg-ncc-gold px-2 py-0.5 text-[10px] font-bold tracking-wider text-slate-900">CONTRACTOR</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-heading font-bold text-slate-900">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{c.contact_phone}</td>
                  <td className="px-3 py-2 font-mono text-amber-800">{c.location || <em className="text-amber-600">(blank)</em>}</td>
                  <td className="px-3 py-2">
                    <input
                      value={d.city}
                      onChange={(e) => setDrafts({ ...drafts, [c.id]: { ...d, city: e.target.value } })}
                      placeholder="e.g. Pune"
                      data-testid={`missing-loc-city-${c.id}`}
                      disabled={isBusy}
                      className="w-32 border-2 border-slate-900 bg-white px-2 py-1.5 font-mono text-sm focus:outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      value={d.state}
                      onChange={(e) => setDrafts({ ...drafts, [c.id]: { ...d, state: e.target.value } })}
                      placeholder="e.g. Maharashtra"
                      data-testid={`missing-loc-state-${c.id}`}
                      disabled={isBusy}
                      className="w-36 border-2 border-slate-900 bg-white px-2 py-1.5 font-mono text-sm focus:outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => save(c.id)}
                      disabled={isBusy || !d.city?.trim() || !d.state?.trim()}
                      data-testid={`missing-loc-save-${c.id}`}
                      className="inline-flex items-center gap-1 border-2 border-slate-900 bg-ncc-gold px-3 py-1.5 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                    >
                      {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export function ExcelTools({ resource = "contractors", segment = "all", showImport = true }) {
  const [busy, setBusy] = useState(null);
  const fileInputRef = React.useRef(null);
  const segLabel = segment === "payroll" ? "Payroll" : segment === "contractor" ? "Contractor" : "";

  async function downloadFile(path, filename) {
    setBusy(path);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/${resource}/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created, skipped, errors } = r.data;
      toast.success(`Imported ${created} record(s)${skipped ? ` · ${skipped} skipped` : ""}`);
      if (errors && errors.length) {
        console.warn("Import errors:", errors);
      }
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <>
      <button type="button" data-testid={`excel-export-btn-${segment}`} disabled={busy !== null}
        onClick={() => downloadFile(`/admin/${resource}/export-excel?segment=${segment}`, `${resource}-${segment}-${new Date().toISOString().slice(0, 10)}.xlsx`)}
        className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export {segLabel ? `${segLabel} Excel` : "Excel"}
      </button>
      {showImport && (
        <>
          <button type="button" data-testid={`excel-template-btn-${segment}`} disabled={busy !== null}
            onClick={() => downloadFile(`/admin/${resource}/import-template`, `${resource}-${segment}-import-template.xlsx`)}
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50">
            <Download className="h-4 w-4" /> {segLabel ? `${segLabel} Template` : "Template"}
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 border-2 border-slate-900 bg-blueprint px-3 py-2 industrial-label text-white transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm">
            {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Import {segLabel ? `${segLabel} Excel` : "Excel"}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm" onChange={handleFile} data-testid={`excel-import-input-${segment}`} className="hidden" />
          </label>
        </>
      )}
    </>
  );
}

function ReadySignalsBanner() {
  const { user } = useAuth();
  const canViewStats = !!user && (user.is_super || (user.permissions || []).includes("view_stats"));
  const [signals, setSignals] = useState([]);
  const [open, setOpen] = useState(false);
  const [acking, setAcking] = useState(null);
  const seenIdsRef = React.useRef(new Set());
  const firstLoadRef = React.useRef(true);

  useEffect(() => {
    if (!canViewStats) return;  // Ready-signals endpoint requires view_stats; skip silently
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get("/admin/ready-signals");
        if (cancelled) return;
        const arr = r.data || [];
        // Detect newly-arrived signals (not seen on the previous poll).
        const currentIds = new Set(arr.map((s) => s.id));
        const newOnes = arr.filter((s) => !seenIdsRef.current.has(s.id));
        if (!firstLoadRef.current && newOnes.length > 0) {
          // Burst pop-up + audible toast for each new signal
          newOnes.forEach((s) => {
            const isPay = s.is_payroll;
            const verb = (s.ready_signal_type === "join" || isPay) ? "Ready to Join" : "Ready to Mobilize";
            const where = [s.city, s.state].filter(Boolean).join(", ") || "—";
            const nos = s.ready_nos ? ` · ${s.ready_nos} nos` : "";
            const when = s.ready_date ? ` · ${s.ready_date}` : "";
            toast.success(`${isPay ? "🟢" : "🟡"} ${s.name} (${where}) — ${verb}${nos}${when}`, {
              duration: 8000,
              icon: "🔔",
            });
          });
          // Open the full review modal automatically so admin sees details immediately
          setOpen(true);
          // Soft beep — uses the Web Audio API; falls back silently if unsupported.
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              const o = ctx.createOscillator();
              const g = ctx.createGain();
              o.connect(g); g.connect(ctx.destination);
              o.type = "sine"; o.frequency.value = 880;
              g.gain.setValueAtTime(0.001, ctx.currentTime);
              g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
              g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
              o.start(); o.stop(ctx.currentTime + 0.32);
              setTimeout(() => ctx.close(), 500);
            }
          } catch { /* no-op */ }
        } else if (firstLoadRef.current && arr.length > 0) {
          // First-load: just show the banner; no toast spam.
          setOpen(true);
        }
        seenIdsRef.current = currentIds;
        firstLoadRef.current = false;
        setSignals(arr);
      } catch { /* silent */ }
    }
    load();
    const t = setInterval(load, 25 * 1000); // poll every 25 seconds
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [canViewStats]);

  if (!canViewStats) return null;

  async function acknowledge(cid) {
    setAcking(cid);
    try {
      await api.post(`/admin/ready-signals/${cid}/acknowledge`);
      setSignals((s) => s.filter((x) => x.id !== cid));
      seenIdsRef.current.delete(cid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to acknowledge");
    } finally {
      setAcking(null);
    }
  }

  if (signals.length === 0) return null;

  return (
    <>
      <div className="mt-4 flex items-center gap-3 border-2 border-emerald-600 bg-emerald-50 px-4 py-3 print:hidden" data-testid="ready-signals-banner">
        <Bell className="h-5 w-5 text-emerald-700" />
        <div className="industrial-label text-emerald-800">{signals.length} contractor(s) ready</div>
        <div className="font-mono text-xs text-emerald-700 hidden sm:block">— click to review</div>
        <button type="button" onClick={() => setOpen(true)} data-testid="ready-signals-open"
          className="ml-auto inline-flex items-center gap-2 border-2 border-emerald-700 bg-white px-3 py-1.5 industrial-label text-emerald-800 hover:-translate-y-0.5 hover:bg-emerald-100 transition-all">
          View
        </button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="ready-signals-modal">
          <div className="w-full max-w-2xl border-2 border-emerald-600 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b-2 border-emerald-600 bg-emerald-50 px-5 py-3">
              <div className="flex items-center gap-2">
                <Send className="h-5 w-5 text-emerald-700" />
                <div className="font-heading text-lg font-extrabold text-emerald-800">Ready Signals · {signals.length}</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ready-signals-close" className="rounded p-1 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr className="border-b-2 border-slate-900">
                    {["Type", "Signal", "Name", "Phone", "Location", "Trades", "Nos", "Ready Date", "When", ""].map((h) => (
                      <th key={h} className="px-3 py-2 text-left industrial-label text-slate-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {signals.map((s) => (
                    <tr key={s.id} className="border-b border-slate-200" data-testid={`ready-signal-row-${s.id}`}>
                      <td className="px-3 py-2">
                        {s.is_payroll
                          ? <span className="border border-slate-900 bg-blueprint px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-white">PAYROLL</span>
                          : <span className="border border-slate-900 bg-ncc-gold px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-slate-900">CONTRACTOR</span>}
                      </td>
                      <td className="px-3 py-2"><span className="border-2 border-emerald-700 bg-emerald-100 px-2 py-0.5 text-[11px] font-bold tracking-wider text-emerald-800">{s.ready_signal_type === "join" ? "READY TO JOIN" : "READY TO MOBILIZE"}</span></td>
                      <td className="px-3 py-2 font-heading font-bold text-slate-900">{s.name}</td>
                      <td className="px-3 py-2 font-mono">{s.contact_phone}</td>
                      <td className="px-3 py-2 font-mono text-xs">{s.location}</td>
                      <td className="px-3 py-2 font-mono text-xs">{(s.trades || []).join(", ")}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`ready-signal-nos-${s.id}`}>
                        {s.ready_nos ? <b className="text-ncc-navy">{s.ready_nos}</b> : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs" data-testid={`ready-signal-date-${s.id}`}>
                        {s.ready_date || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{s.ready_signaled_at ? new Date(s.ready_signaled_at).toLocaleString() : ""}</td>
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => acknowledge(s.id)} disabled={acking === s.id}
                          data-testid={`ready-signal-ack-${s.id}`}
                          className="inline-flex items-center gap-1 border-2 border-slate-900 bg-ncc-gold px-2 py-1 industrial-label hover:-translate-y-0.5 transition-all disabled:opacity-50">
                          {acking === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                          OK
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// ============================================================
// EVENTS · WEATHER  (moved out of front-page Dashboard in v1.7)
// Rendered inside Reports → Events · Weather tab. Self-contained:
// loads /api/dashboard once so FestivalTracker + HarvestingAlert can
// read their `data.festivals` / `data.harvesting` slots; the
// UpcomingEvents component + StateWeather widget fetch their own data.
// ============================================================
export function EventsReportPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/dashboard", { params: { segment: "all" }, silent: true });
      setData(r.data || null);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="flex flex-col gap-6" data-testid="events-report-panel">
      <div className="flex items-center justify-between border-b-2 border-slate-900 pb-3">
        <div>
          <div className="industrial-label text-[10px] text-slate-500">// Reports · Events · Weather</div>
          <h2 className="font-heading text-2xl font-black text-ncc-navy">Events, Festivals &amp; Weather</h2>
          <p className="text-xs text-slate-600 mt-1">Mobilisation-planning intelligence — regional festivals, harvest cycles, live weather, and upcoming national events.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          data-testid="events-report-refresh"
          className="flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label text-xs shadow-brutal-sm hover:bg-slate-100"
        >
          <RefreshIcon className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      {/* Row 1: Festival + Harvest side-by-side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <FestivalTracker data={data} />
        <HarvestingAlert data={data} />
      </div>
      {/* Row 2: Upcoming LLM-generated events (elections + emergencies + monsoon) */}
      <UpcomingEvents />
      {/* Row 3: Live multi-state weather (moved out of Footer in v1.7) */}
      <div className="border-2 border-slate-900 bg-ncc-navy p-4 shadow-brutal-sm">
        <StateWeather />
      </div>
    </div>
  );
}
// Shim for the refresh icon so we don't need another lucide import at
// the top of a file that already imports many icons. Reuses the same
// RefreshCw shape via a tiny inline SVG (also gives us fine-grained
// animation control without extra props).
function RefreshIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M23 4v6h-6" />
      <path d="M20.49 15A9 9 0 1 1 5.64 5.64L23 4" />
    </svg>
  );
}

